import React from 'react';
import { superAdminTooltips } from '../../../../shared/config/actionHelpCatalog';
import HelpTooltipTrigger from '../../../../shared/ui/common/HelpTooltipTrigger';
import { formatAdminUtcDate } from '../../lib/adminDates';
import type { AdminDemoLeadDetail } from '../../legalTypes';
import { AdminButton, EntityDrawer, StatusPill } from '../components';

interface DetailValueProps {
  label: string;
  children: React.ReactNode;
  sensitive?: boolean;
}

const DetailValue: React.FC<DetailValueProps> = ({ label, children, sensitive = false }) => (
  <div className="admin-legal-detail-value">
    <dt>{label}</dt>
    <dd data-visual-mask={sensitive ? true : undefined}>{children || '—'}</dd>
  </div>
);

interface DemoLeadDetailDrawerProps {
  lead: AdminDemoLeadDetail | null;
  loading: boolean;
  onClose: () => void;
  onConvert: () => void;
  onStatus: (status: string) => void;
  mutating: boolean;
}

const DemoLeadDetailDrawer: React.FC<DemoLeadDetailDrawerProps> = ({
  lead,
  loading,
  onClose,
  onConvert,
  onStatus,
  mutating,
}) => (
  <EntityDrawer
    open={loading || lead !== null}
    title={lead?.companyName ?? 'Loading lead'}
    ariaLabel={lead?.companyName ?? 'Demo lead'}
    eyebrow="Demo lead review"
    onClose={onClose}
    actions={lead && (
      <div className="admin-legal-inline-actions">
        <HelpTooltipTrigger label="Convert reviewed lead" help={superAdminTooltips.demoLeadConversion} placement="bottom-start" />
        <AdminButton
          variant="primary"
          disabled={lead.status !== 'reviewing' || mutating}
          onClick={onConvert}
        >
          Convert reviewed lead
        </AdminButton>
        <AdminButton
          disabled={lead.status === 'reviewing' || lead.status === 'converted' || mutating}
          onClick={() => onStatus('reviewing')}
        >
          Mark as reviewing
        </AdminButton>
        <AdminButton
          variant="danger"
          disabled={lead.status === 'converted' || lead.status === 'rejected' || mutating}
          onClick={() => onStatus('rejected')}
        >
          Reject lead
        </AdminButton>
      </div>
    )}
  >
    {loading && !lead ? (
      <div role="status" aria-label="Loading demo lead">Loading authorized lead details…</div>
    ) : lead ? (
      <div className="admin-legal-detail-stack">
        <div className="admin-legal-summary-line">
          <StatusPill tone={lead.status === 'converted' ? 'success' : 'info'}>
            [{lead.status === 'converted' ? '✓' : '•'}] {lead.status.replace(/_/g, ' ')}
          </StatusPill>
          <span>Version <span data-visual-mask>{lead.version}</span></span>
        </div>
        <dl className="admin-legal-detail-grid">
          <DetailValue label="Contact" sensitive>{lead.firstName} {lead.lastName}</DetailValue>
          <DetailValue label="Email" sensitive>{lead.email}</DetailValue>
          <DetailValue label="Phone" sensitive>{lead.phone}</DetailValue>
          <DetailValue label="Job title">{lead.jobTitle}</DetailValue>
          <DetailValue label="Country">{lead.country}</DetailValue>
          <DetailValue label="Sector">{lead.sector}</DetailValue>
          <DetailValue label="Company size">{lead.companySize}</DetailValue>
          <DetailValue label="Requested demo">
            {lead.requestedDemoAtUtc ? formatAdminUtcDate(lead.requestedDemoAtUtc) : 'Not requested'}
          </DetailValue>
        </dl>
        <section className="admin-legal-copy-section" aria-labelledby="demo-lead-motivation">
          <h3 id="demo-lead-motivation">Review notes</h3>
          <p>{lead.motivation || 'No motivation was supplied.'}</p>
          {lead.workflowDescription && <p>{lead.workflowDescription}</p>}
          {lead.customizationRequirements && <p>{lead.customizationRequirements}</p>}
          {lead.integrationRequirements && <p>{lead.integrationRequirements}</p>}
        </section>
        {lead.status === 'converted' && (
          <div className="admin-legal-callout admin-legal-callout--success" role="status">
            This lead is already converted. Its customer and legal package are retained.
          </div>
        )}
      </div>
    ) : null}
  </EntityDrawer>
);

export default DemoLeadDetailDrawer;
